import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api, fileUrl } from "../api";
import GatheringRow from "../components/GatheringRow";
import Newsletter from "../components/Newsletter";
import {
  Rule,
  Footer,
  Loading,
  Nav,
  PageHead,
  Verse,
  ErrorNote,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const fetcher = (url) => api.get(url).then((r) => r.data);

function EmptyCalendar() {
  return (
    <div className="empty" data-testid="gatherings-empty-state">
      <h3 style={{ marginBottom: ".8rem" }}>Nothing on the calendar, for now.</h3>
      <p>
        We don&rsquo;t keep it busy on purpose. Leave your name and we&rsquo;ll
        tell you when the next table is set.
      </p>
      <p style={{ marginTop: "1.4rem" }}>
        <Link to="/join" className="linkish" data-testid="gatherings-empty-newsletter-link">
          Get the next note &rarr;
        </Link>
        <span style={{ color: "var(--ink-mute)", margin: "0 .8rem" }}>&middot;</span>
        <Link to="/rule" className="linkish" data-testid="gatherings-empty-rule-link">
          Read the Rule &rarr;
        </Link>
      </p>
    </div>
  );
}

export default function Gatherings() {
  useBodyClass("inner");
  useDocumentTitle(
    "Gatherings",
    "What's coming up with Sch\u0113ma: circles, showings and one-offs in Calgary.",
  );

  const upcoming = useSWR("/gatherings?scope=upcoming", fetcher);
  const past = useSWR("/gatherings?scope=past", fetcher);

  const list = upcoming.data?.gatherings || [];
  const archive = past.data?.gatherings || [];

  return (
    <>
      <Nav />
      <PageHead
        eyebrow="What's on"
        title="Gatherings"
        standfirst="Circles meet monthly-ish. Showings and one-offs happen when there's a reason for them. If a date is listed, you're welcome at it."
        testid="gatherings-page-head"
      />

      <section data-testid="gatherings-upcoming">
        <div className="prose">
          {upcoming.isLoading ? (
            <Loading label="Reading the calendar" testid="gatherings-loading" />
          ) : upcoming.error ? (
            <ErrorNote
              message="We couldn't reach the calendar just now."
              onRetry={() => upcoming.mutate()}
              testid="gatherings-error"
            />
          ) : list.length === 0 ? (
            <EmptyCalendar />
          ) : (
            <>
              {list.map((g) => (
                <GatheringRow key={g.id} g={g} />
              ))}
              <p style={{ marginTop: "1.8rem" }}>
                <a
                  className="linkish"
                  href={fileUrl("/gatherings/calendar.ics")}
                  data-testid="gatherings-subscribe-all-link"
                >
                  Add everything to your calendar (.ics)
                </a>
              </p>
            </>
          )}
        </div>
      </section>

      {archive.length > 0 ? (
        <>
          <Rule />
          <section data-testid="gatherings-past">
            <div className="prose">
              <p className="eyebrow sc">
                <span className="num">&middot;</span>
                <span>Already happened</span>
              </p>
              <h2>The archive.</h2>
              <p className="path-blurb">
                What the room has actually done, rather than what it means to do.
              </p>
              {archive.map((g) => (
                <GatheringRow key={g.id} g={g} past />
              ))}
            </div>
          </section>
        </>
      ) : null}

      <Rule />

      <section data-testid="gatherings-kinds">
        <div className="wrap">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>What you&rsquo;d be walking into</span>
          </p>
          <h2>The kinds of things we do.</h2>
          <div className="grid four" style={{ marginTop: "2.2rem" }}>
            <div className="cell">
              <span className="sc">Circles</span>
              <h3>Three to six people</h3>
              <p>
                Who share a medium, meeting monthly-ish. You bring work in
                progress and leave with something to try.
              </p>
            </div>
            <div className="cell">
              <span className="sc">Showings</span>
              <h3>Receive, don&rsquo;t critique</h3>
              <p>
                Someone reads, or plays, or hangs a piece, and we sit with it. No
                feedback round unless it&rsquo;s asked for.
              </p>
            </div>
            <div className="cell">
              <span className="sc">One-offs</span>
              <h3>A concert, a gallery night</h3>
              <p>
                An experimental worship service that may well not work. A workshop
                with someone who knows something we don&rsquo;t.
              </p>
            </div>
            <div className="cell">
              <span className="sc">Neighbourhoods</span>
              <h3>Art for the margins</h3>
              <p>
                Down the road, ways to use art to bless those in the margins
                &mdash; all the margins. No saviour complex allowed.
              </p>
            </div>
          </div>
          <p style={{ marginTop: "1.6rem" }}>
            The fuller account of each of these is in{" "}
            <Link to="/rule">the Rule</Link>.
          </p>
        </div>
      </section>

      <Rule />

      <section data-testid="gatherings-newsletter">
        <div className="prose">
          <Newsletter
            heading="Want to come to one?"
            promise="Tell us and we'll write to you when the next thing is booked."
            source="gatherings"
            testidPrefix="gatherings-newsletter"
          />
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
